// src/components/Services/ServiceOrders/ConvertToSaleDialog.tsx
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useConvertServiceOrderToSale } from '@/hooks/useConvertServiceOrderToSale';
import { formatCurrency } from '@/utils/formatters';
import { safeFormatDate } from '@/utils/dateHelpers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2 } from 'lucide-react';

// ─── Schema ──────────────────────────────────────────────────────────────────

const PAYMENT_METHODS = [
  { value: 'pix',            label: 'PIX' },
  { value: 'boleto',         label: 'Boleto' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito',  label: 'Cartão de Débito' },
  { value: 'dinheiro',       label: 'Dinheiro' },
  { value: 'transferencia',  label: 'Transferência' },
] as const;

const schema = z.object({
  salesperson_id:     z.string().uuid('Selecione um vendedor'),
  payment_method:     z.enum([
    'pix', 'boleto', 'cartao_credito', 'cartao_debito', 'dinheiro', 'transferencia',
  ]),
  installments_count: z.coerce.number().int().min(1).max(12),
  first_due_date:     z.string().min(1, 'Informe o vencimento'),
});

type FormValues = z.infer<typeof schema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface ConvertToSaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceOrder: {
    id: string;
    service_order_number: string;
    total_value: number;
    customer_id: string;
  };
  companyId: string;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function ConvertToSaleDialog({
  open,
  onOpenChange,
  serviceOrder,
}: ConvertToSaleDialogProps) {
  // ── Todos os hooks ANTES de qualquer early return ──────────────────
  const convertMutation = useConvertServiceOrderToSale();

  const { data: salespeople = [], isLoading: loadingSalespeople } = useQuery({
    queryKey: ['profiles-commissioned'],
    queryFn: () =>
      supabase
        .rpc('get_profiles_with_feature_enabled', {
          p_feature_key: 'sales:is_commissioned',
        })
        .throwOnError()
        .then((r) => r.data ?? []),
    enabled: open,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      salesperson_id:     '',
      payment_method:     'pix',
      installments_count: 1,
      first_due_date:     '',
    },
  });

  const installmentsCount = form.watch('installments_count');
  const firstDueDate      = form.watch('first_due_date');
  const totalValue        = serviceOrder.total_value ?? 0;

  const calculatedInstallments = useMemo(() => {
    const count = Math.max(1, Number(installmentsCount) || 1);
    if (!firstDueDate) return [];

    const base = Math.round((totalValue / count) * 100) / 100;
    const last = Math.round((totalValue - base * (count - 1)) * 100) / 100;

    return Array.from({ length: count }, (_, i) => {
      const d = new Date(firstDueDate);
      d.setMonth(d.getMonth() + i);
      return {
        installment_number: i + 1,
        amount:             i === count - 1 ? last : base,
        due_date:           d.toISOString().split('T')[0],
      };
    });
  }, [installmentsCount, firstDueDate, totalValue]);
  // ─────────────────────────────────────────────────────────────────

  const onConfirm = () => {
    if (!form.formState.isValid) {
      form.trigger();
      return;
    }
    const values = form.getValues();
    convertMutation.mutate(
      {
        serviceOrderId: serviceOrder.id,
        salespersonId:  values.salesperson_id,
        paymentMethod:  values.payment_method,
        installments:   calculatedInstallments,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          form.reset();
        },
      },
    );
  };

  const showPreview = !!firstDueDate && installmentsCount >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Converter OS #{serviceOrder.service_order_number} em Venda
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-4 py-2">
            {/* Total */}
            <div className="flex items-center justify-between rounded-md border bg-muted/40 px-4 py-2">
              <span className="text-sm text-muted-foreground">Total da OS</span>
              <span className="font-semibold">{formatCurrency(totalValue)}</span>
            </div>

            {/* Vendedor */}
            <FormField
              control={form.control}
              name="salesperson_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendedor</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingSalespeople
                              ? 'Carregando...'
                              : 'Selecione um vendedor'
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(salespeople as Array<{ id: string; full_name: string }>).map(
                        (sp) => (
                          <SelectItem key={sp.id} value={sp.id}>
                            {sp.full_name}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Forma de pagamento */}
            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Forma de Pagamento</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PAYMENT_METHODS.map((pm) => (
                        <SelectItem key={pm.value} value={pm.value}>
                          {pm.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Número de parcelas + Primeiro vencimento */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="installments_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de Parcelas</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={12}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="first_due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primeiro Vencimento</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Preview de parcelas */}
            {showPreview && calculatedInstallments.length > 0 && (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Nº</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Vencimento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calculatedInstallments.map((inst) => (
                      <TableRow key={inst.installment_number}>
                        <TableCell className="font-medium">
                          {inst.installment_number}
                        </TableCell>
                        <TableCell>{formatCurrency(inst.amount)}</TableCell>
                        <TableCell>
                          {safeFormatDate(inst.due_date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={convertMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={convertMutation.isPending}
          >
            {convertMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Convertendo…
              </>
            ) : (
              'Confirmar Conversão'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
